import { ancestor } from 'acorn-walk'

import { replaceNode, t } from './utils'

import type { AnyNode, Node } from 'acorn'

export function transformEsm<T extends AnyNode>(node: T): T
export function transformEsm(node: AnyNode): AnyNode {
  ancestor(node, {
    ExportAllDeclaration(node, state, ancestors) {
      if (node.exported) {
        replaceNode(
          ancestors,
          t.expressionStatement(
            t.assignmentExpression(
              '=',
              t.memberExpression(
                t.identifier('module'),
                t.memberExpression(t.identifier('exports'), node.exported)
              ),
              t.callExpression(t.identifier('require'), [node.source])
            )
          )
        )
      } else {
        replaceNode(
          ancestors,
          t.expressionStatement(
            t.callExpression(
              t.memberExpression(
                t.identifier('Object'),
                t.identifier('assign')
              ),
              [
                t.memberExpression(
                  t.identifier('module'),
                  t.identifier('exports')
                ),
                t.callExpression(t.identifier('require'), [node.source])
              ]
            )
          )
        )
      }
    },
    ExportDefaultDeclaration(node, state, ancestors) {
      replaceNode(
        ancestors,
        t.expressionStatement(
          t.assignmentExpression(
            '=',
            t.memberExpression(
              t.identifier('module'),
              t.memberExpression(
                t.identifier('exports'),
                t.identifier('default')
              )
            ),
            node.declaration
          )
        )
      )
    },
    ExportNamedDeclaration(node, state, ancestors) {
      const createModuleExports = (
        identifier?: Node | null,
        right?: Node | null
      ) => {
        if (!identifier || !right) return
        return t.expressionStatement(
          t.assignmentExpression(
            '=',
            t.memberExpression(
              t.identifier('module'),
              t.memberExpression(t.identifier('exports'), identifier)
            ),
            right
          )
        )
      }
      if (node.declaration) {
        if (
          t.isFunctionDeclaration(node.declaration) ||
          t.isClassDeclaration(node.declaration)
        ) {
          replaceNode(
            ancestors,
            createModuleExports(node.declaration.id, node.declaration)
          )
        } else if (t.isVariableDeclaration(node.declaration)) {
          replaceNode(
            ancestors,
            node.declaration.declarations.map((declaration) =>
              createModuleExports(declaration.id, declaration.init)
            )
          )
        }
      } else if (node.specifiers.length > 0) {
        replaceNode(
          ancestors,
          node.specifiers.map((specifier) =>
            createModuleExports(
              specifier.exported,
              node.source
                ? t.memberExpression(
                    t.callExpression(t.identifier('require'), [node.source]),
                    specifier.local
                  )
                : specifier.local
            )
          )
        )
      }
    },
    ImportDeclaration(node, state, ancestors) {
      if (node.specifiers.length > 0) {
        if (t.isImportNamespaceSpecifier(node.specifiers[0])) {
          replaceNode(
            ancestors,
            t.variableDeclaration('const', [
              t.variableDeclarator(
                node.specifiers[0].local,
                t.callExpression(t.identifier('require'), [
                  t.literal(node.source.value)
                ])
              )
            ])
          )
        } else {
          const declarations: Node[] = []
          const properties: Node[] = []
          for (const specifier of node.specifiers) {
            if (
              t.isImportDefaultSpecifier(specifier) ||
              (t.isImportSpecifier(specifier) &&
                t.isIdentifier(specifier.imported) &&
                specifier.imported.name === 'default')
            ) {
              declarations.push(
                t.variableDeclaration('const', [
                  t.variableDeclarator(
                    specifier.local,
                    t.callExpression(t.identifier('__interopRequireDefault'), [
                      t.callExpression(t.identifier('require'), [
                        t.literal(node.source.value)
                      ])
                    ])
                  )
                ])
              )
            } else if (t.isImportSpecifier(specifier)) {
              properties.push(t.property(specifier.imported, specifier.local))
            }
          }
          if (properties.length > 0) {
            declarations.push(
              t.variableDeclaration('const', [
                t.variableDeclarator(
                  t.objectPattern(properties),
                  t.callExpression(t.identifier('require'), [
                    t.literal(node.source.value)
                  ])
                )
              ])
            )
          }
          if (declarations.length > 0) {
            replaceNode(ancestors, declarations)
          }
        }
      } else {
        replaceNode(
          ancestors,
          t.expressionStatement(
            t.callExpression(t.identifier('require'), [
              t.literal(node.source.value)
            ])
          )
        )
      }
    },
    ImportExpression(node, state, ancestors) {
      replaceNode(
        ancestors,
        t.callExpression(t.identifier('asyncRequire'), [node.source])
      )
    }
  })
  return node
}
